import React, { useState } from "react";
import { Eye, Target, ShieldCheck, MapPin, Phone, Mail, Award, ArrowRight } from "lucide-react";

const Stitch = ({ className = "", color = "#2E7D32" }) => (
  <svg className={className} viewBox="0 0 100 4" preserveAspectRatio="none">
    <line
      x1="0" y1="2" x2="100" y2="2"
      stroke={color} strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
    />
  </svg>
);

const pillars = [
  {
    icon: Eye,
    title: "Our Vision",
    text: "To create an inclusive and self-reliant society by empowering people through quality education, employable skills, sustainable livelihoods, and community development.",
  },
  {
    icon: Target,
    title: "Our Mission",
    text: "To provide opportunities for skill development, vocational education, entrepreneurship, employment generation, and social upliftment for women, youth, rural communities, and other disadvantaged groups.",
  },
  {
    icon: ShieldCheck,
    title: "Our Core Values",
    text: "Voluntary and non-profit operation, non-political commitment, environmental consciousness, absolute compliance, and empowering the most vulnerable first.",
  },
];

const AboutPage = () => {
  return (
    <div className="bg-[#FBF9F3] min-h-screen pt-[84px] pb-16">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@500&display=swap');
        .mbf-display { font-family: 'Fraunces', serif; }
        .mbf-body { font-family: 'IBM Plex Sans', sans-serif; }
        .mbf-mono { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      {/* Standalone Page Hero */}
      <section className="bg-[#082440] text-white py-20 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 34px), repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 34px)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <span className="mbf-mono text-[12px] tracking-[0.2em] uppercase text-[#E0A526]">
            Know Our Foundation
          </span>
          <h1 className="mbf-display text-4xl sm:text-5xl font-semibold mt-4 tracking-tight">
            About MB Foundation
          </h1>
          <Stitch className="w-32 h-2 mx-auto mt-6" color="#E0A526" />
          <p className="mbf-body mt-6 text-blue-100/70 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            M.B. Foundation of Skill Development &amp; Livelihood (MBFSDL) is a registered charitable public trust established on 18 September 2015.
          </p>
        </div>
      </section>

      {/* Our History / Trust Background */}
      <section className="py-20 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Image with stitched frame */}
          <div className="lg:col-span-5 relative">
            <div className="relative border border-[#0A355C]/15 p-3 bg-white">
              <div className="overflow-hidden">
                <img
                  src="/images/about.jpg"
                  alt="Women empowerment workshop by MBFSDL"
                  className="w-full h-[450px] object-cover"
                />
              </div>
              <span className="absolute -top-[3px] -left-[3px] w-4 h-4 border-t-2 border-l-2 border-[#2E7D32]" />
              <span className="absolute -bottom-[3px] -right-[3px] w-4 h-4 border-b-2 border-r-2 border-[#2E7D32]" />
            </div>
            
            {/* Overlay ticket tag */}
            <div
              className="absolute -bottom-6 -right-6 bg-[#E0A526] text-[#0A355C] p-6 max-w-[210px] shadow-xl"
              style={{ clipPath: "polygon(0 0, 100% 0, 100% 82%, 84% 100%, 0 100%)" }}
            >
              <h4 className="mbf-display text-4xl font-semibold leading-none">2015</h4>
              <p className="mbf-mono text-[10px] tracking-wider uppercase mt-2 font-semibold">Established</p>
              <p className="mbf-body text-[12px] text-[#0A355C]/80 mt-1 leading-relaxed">
                Registered under the Indian Trusts Act.
              </p>
            </div>
          </div>

          {/* Right Column: Detailed Story */}
          <div className="lg:col-span-7">
            <span className="mbf-mono text-[12px] tracking-[0.18em] uppercase text-[#2E7D32]">
              Our Foundation Story
            </span>
            <h2 className="mbf-display text-3xl sm:text-4xl font-semibold text-[#0A355C] mt-3">
              Serving Communities Without Any Profit Motive
            </h2>
            <Stitch className="w-24 h-2 mt-4" color="#2E7D32" />
            
            <div className="mbf-body mt-6 space-y-6 text-[#514a3d] text-[15px] leading-relaxed">
              <p>
                Founded with the objective of empowering individuals and communities through education, skill development, livelihood generation, and social welfare initiatives, the Trust operates as a registered, non-profit, non-political, and voluntary organization.
              </p>
              <p>
                Our registered office is located at <strong>No. 2/116 C, First Street, Samy Nagar, Bagayam, Vellore – 632002, Tamil Nadu</strong>. From this base, we develop compliance-focused, high-impact programs designed to improve the standard of living of rural youth, women, tribal families, and minorities.
              </p>
              <p>
                We believe that education must lead to self-reliance. By partnering with governmental bodies and local industries, we offer training paths that build real-world capability, helping trainees secure employment or start their own self-sufficient cottage enterprises.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision, Mission, Approach Cards */}
      <section className="bg-[#F2ECDD] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="mbf-mono text-[12px] tracking-[0.18em] uppercase text-[#2E7D32]">
              Core Pillars
            </span>
            <h2 className="mbf-display mt-3 text-3xl font-semibold text-[#0A355C]">
              What Drives Our Work
            </h2>
            <Stitch className="w-24 h-2 mx-auto mt-4" color="#2E7D32" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map(({ icon: Icon, title, text }, i) => (
              <div
                key={i}
                className="bg-white border border-[#0A355C]/10 p-8 relative flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow"
              >
                <span className="absolute top-0 left-6 right-6 h-1 bg-[#2E7D32]" />
                <div className="w-12 h-12 rounded-full border border-[#0A355C] flex items-center justify-center text-[#2E7D32] bg-[#FBF9F3] mb-6">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="mbf-display text-xl font-semibold text-[#0A355C] mb-4">
                  {title}
                </h3>
                <p className="mbf-body text-[#6b6355] text-[14px] leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Registration & Legal Compliance */}
      <section className="py-20 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="bg-white border border-[#0A355C]/10 p-8 md:p-12 relative">
          <span className="absolute -top-[3px] -left-[3px] w-6 h-6 border-t-4 border-l-4 border-[#2E7D32]" />
          <span className="absolute -bottom-[3px] -right-[3px] w-6 h-6 border-b-4 border-r-4 border-[#E0A526]" />
          
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <span className="mbf-mono text-[11px] uppercase tracking-wider text-[#2E7D32]">
                Voluntary Compliance
              </span>
              <h3 className="mbf-display text-2xl sm:text-3xl font-semibold text-[#0A355C] mt-2">
                A Reliable Partner for Philanthropic and CSR Investment
              </h3>
              <p className="mbf-body text-[#514a3d] text-sm sm:text-base mt-4 leading-relaxed">
                As a registered public charitable trust, MB Foundation is fully compliant with legal, auditing, and tax standards. We provide transparent reports, detailed impact metrics, and complete documentation for corporate partners, developmental bodies, and institutional donors.
              </p>
            </div>
            
            <div className="md:col-span-4 flex flex-col gap-4">
              <div className="border border-[#0A355C]/10 p-4 bg-[#FBF9F3]/50">
                <h4 className="mbf-mono text-xs font-semibold text-[#0A355C] uppercase">Registered Trust</h4>
                <p className="mbf-body text-xs text-[#6b6355] mt-1">Date: 18 September 2015</p>
              </div>
              <div className="border border-[#0A355C]/10 p-4 bg-[#FBF9F3]/50">
                <h4 className="mbf-mono text-xs font-semibold text-[#0A355C] uppercase">Headquarters</h4>
                <p className="mbf-body text-xs text-[#6b6355] mt-1">Bagayam, Vellore, Tamil Nadu</p>
              </div>
              <a
                href="#contact"
                className="mbf-body text-center bg-[#0A355C] hover:bg-[#082440] text-white py-3 px-6 font-semibold uppercase text-xs tracking-wider transition shadow-sm"
              >
                Inquire for Partnerships
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
